import React, { useMemo, useState } from 'react'
import { DateTime, Interval } from 'luxon'
import { Link } from 'react-router-dom'
import { useQuery, gql } from '@apollo/client'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import Chip from '@material-ui/core/Chip'
import Divider from '@material-ui/core/Divider'
import LinearProgress from '@material-ui/core/LinearProgress'
import TextField from '@material-ui/core/TextField'
import InputAdornment from '@material-ui/core/InputAdornment'
import CustomLuxonAdapter from 'lib/CustomLuxonAdapter'
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import themes from 'themes'
import { currency } from 'lib/utils/string'
import ExportTransactionsDialog from './export-transactions-dialog'
import Error from 'components/error'
import { PageHeader } from 'components/page-header'
import AccountCard from 'pages/account/account-details/account-card'
import AttachMoneyIcon from 'components/material-symbol/icons/AttachMoney'
import MoneyOffIcon from 'components/material-symbol/icons/MoneyOff'
import EventIcon from 'components/material-symbol/icons/Event'
import ChevronLeftIcon from 'components/material-symbol/icons/ChevronLeft'
import ChevronRightIcon from 'components/material-symbol/icons/ChevronRight'
import ExpandMoreIcon from 'components/material-symbol/icons/ExpandMore'
import FileDownloadOutlinedIcon from 'components/material-symbol/icons/FileDownload'
import CloseIcon from 'components/material-symbol/icons/Close'
import SearchIcon from 'components/material-symbol/icons/Search'
import ReceiptIcon from 'components/material-symbol/icons/Receipt'
import SectionEmptyState from 'components/section-empty-state'
import {
  getPayorName,
  getPaymentStatus,
  getDisplayAmountInCents,
  formatTransactionDate,
  formatMonthRange,
  getTransactionDescription,
  getCompletedIncomeInCents,
  formatSignedAmount,
  generateTransactionsCsvData,
  getTransactionsExportFilename,
  PAYMENT_STATUS,
  PAYMENT_STATUS_LABEL,
} from './transaction-helpers'
import { OUTLINED_INPUT_BORDER_RADIUS } from 'lib/form-field-styles'
import { useAuth } from 'lib/auth'
import { useStyles } from './styles'

const PAYMENTS_PATH = '/account/payments'

const paymentsCrumb = [{ label: 'Payments', to: PAYMENTS_PATH }]

const monthPickerIconProps = {
  fontSize: 'small',
  opticalSize: 20,
}

const monthPickerSlots = {
  leftArrowIcon: ChevronLeftIcon,
  rightArrowIcon: ChevronRightIcon,
  switchViewIcon: ExpandMoreIcon,
}

const monthPickerSlotProps = {
  desktopPaper: {
    sx: {
      borderRadius: `${OUTLINED_INPUT_BORDER_RADIUS}px`,
      '& .MuiPickersYear-yearButton.Mui-disabled': {
        display: 'none',
      },
      '& .MuiPickersMonth-monthButton.Mui-disabled': {
        display: 'none',
      },
    },
  },
  popper: {
    sx: {
      '& .MuiPaper-root': {
        borderRadius: `${OUTLINED_INPUT_BORDER_RADIUS}px`,
      },
      '& .MuiPickersYear-yearButton.Mui-disabled': {
        display: 'none',
      },
      '& .MuiPickersMonth-monthButton.Mui-disabled': {
        display: 'none',
      },
    },
  },
  leftArrowIcon: monthPickerIconProps,
  rightArrowIcon: monthPickerIconProps,
  switchViewIcon: monthPickerIconProps,
}

const filterPaymentsBySearch = (payments, searchQuery) => {
  const query = searchQuery.trim().toLowerCase()
  if (!query) return payments

  return payments.filter(payment => {
    const payorName = (getPayorName(payment.payor) || '').toLowerCase()
    const description = (getTransactionDescription(payment) || '').toLowerCase()

    return payorName.includes(query) || description.includes(query)
  })
}

const MonthPickerControl = ({
  selectedMonth,
  onMonthChange,
  className,
  iconClassName,
}) => {
  const classes = useStyles()
  const [isPickerOpen, setIsPickerOpen] = useState(false)
  const maxSelectableMonth = DateTime.now().endOf('month')

  return (
    <ThemeProvider theme={createTheme(themes.pgaCoach)}>
      <LocalizationProvider dateAdapter={CustomLuxonAdapter}>
        <>
          <Button
            variant="outlined"
            className={className}
            onClick={() => setIsPickerOpen(true)}
            startIcon={
              <EventIcon
                className={iconClassName}
                fontSize="small"
                opticalSize={20}
              />
            }
            aria-label={`Selected month ${selectedMonth.toFormat('LLLL yyyy')}`}
          >
            {selectedMonth.toFormat('LLL yyyy')}
          </Button>
          <Box className={classes.hiddenDatePicker}>
            <DatePicker
              open={isPickerOpen}
              onOpen={() => setIsPickerOpen(true)}
              onClose={() => setIsPickerOpen(false)}
              label="Select Month"
              value={selectedMonth}
              onChange={newValue => {
                if (newValue) onMonthChange(newValue)
                setIsPickerOpen(false)
              }}
              views={['month', 'year']}
              format="LLL yyyy"
              disableFuture
              maxDate={maxSelectableMonth}
              slots={monthPickerSlots}
              slotProps={monthPickerSlotProps}
            />
          </Box>
        </>
      </LocalizationProvider>
    </ThemeProvider>
  )
}

const TransactionSearchField = ({
  searchQuery,
  onSearchQueryChange,
  className,
}) => {
  const classes = useStyles()

  return (
    <TextField
      className={className}
      variant="outlined"
      fullWidth
      placeholder="Search by name"
      value={searchQuery}
      onChange={event => onSearchQueryChange(event.target.value)}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon className={classes.searchIcon} fontSize="small" />
          </InputAdornment>
        ),
      }}
      inputProps={{ 'aria-label': 'Search transactions' }}
    />
  )
}

const TransactionFilterRow = ({
  searchQuery,
  onSearchQueryChange,
  selectedMonth,
  onMonthChange,
}) => {
  const classes = useStyles()

  return (
    <>
      <TransactionSearchField
        searchQuery={searchQuery}
        onSearchQueryChange={onSearchQueryChange}
        className={`${classes.searchField} ${classes.searchFieldInHeader}`}
      />
      <MonthPickerControl
        selectedMonth={selectedMonth}
        onMonthChange={onMonthChange}
        className={classes.monthFilterControl}
        iconClassName={classes.monthFilterIcon}
      />
    </>
  )
}

const GET_REGISTRATION_PAYMENTS = gql`
  query GetRegistrationPayments($createdAt: DateTimeRangeInput) {
    registrationPayments(createdAt: $createdAt) {
      id
      createdAt
      totalInCents
      leftToRefundInCents
      transactions {
        amountInCents
        refund
        sourceType
        createdAt
      }
      payor {
        ... on Contact {
          firstName
          lastName
        }
        ... on Coach {
          name
        }
      }
      products {
        ... on Enrollment {
          lesson {
            title
          }
        }
        ... on LessonPack {
          lessonType {
            title
          }
          lessonPackOffering {
            quantity
          }
        }
        ... on FamilyCupProgram {
          name
        }
        ... on JRLChampionshipSeasonProgram {
          name
        }
        ... on JRLLocalLeagueProgram {
          name
        }
        ... on JRLProgramEnrollment {
          player {
            fullName
          }
          program {
            name
          }
        }
        ... on JRLKitOrder {
          category
          program {
            name
          }
        }
        ... on FamilyCupProgramEnrollment {
          program {
            name
          }
          captain {
            firstName
            lastName
          }
        }
      }
    }
  }
`

const STATUS_CHIP_CLASS = {
  [PAYMENT_STATUS.COMPLETED]: 'completedChip',
  [PAYMENT_STATUS.PENDING]: 'pendingChip',
  [PAYMENT_STATUS.REFUNDED]: 'refundedChip',
}

const useTransactionHistoryQuery = (startDate, endDate) => {
  const createdAtInterval =
    startDate && endDate && Interval.fromDateTimes(startDate, endDate)

  return useQuery(GET_REGISTRATION_PAYMENTS, {
    variables: {
      createdAt: {
        from: createdAtInterval?.start,
        to: createdAtInterval?.end,
      },
    },
    fetchPolicy: 'cache-and-network',
  })
}

const IncomeSummaryCard = ({ selectedMonth, incomeInCents }) => {
  const classes = useStyles()

  return (
    <Box className={classes.card}>
      <Box className={classes.incomeCardContent}>
        <Box>
          <Typography className={classes.incomeLabel}>
            Completed income
          </Typography>
          <Typography className={classes.incomeAmount}>
            {currency(incomeInCents / 100)}
          </Typography>
        </Box>
        <Typography className={classes.incomeDateRange}>
          {formatMonthRange(selectedMonth)}
        </Typography>
      </Box>
    </Box>
  )
}

const TransactionHistoryRow = ({ registrationPayment, isLast }) => {
  const classes = useStyles()
  const { id, payor, createdAt, transactions = [] } = registrationPayment
  const payorName = getPayorName(payor)
  const status = getPaymentStatus(registrationPayment)
  const amountInCents = getDisplayAmountInCents(registrationPayment)
  const description = getTransactionDescription(registrationPayment)
  const refundTransaction = transactions
    .filter(transaction => transaction.refund)
    .sort(
      (left, right) =>
        DateTime.fromISO(right.createdAt).toMillis() -
        DateTime.fromISO(left.createdAt).toMillis(),
    )[0]
  const formattedDate = formatTransactionDate(
    refundTransaction?.createdAt || createdAt,
  )
  const isRefund = status === PAYMENT_STATUS.REFUNDED
  const formattedAmount = formatSignedAmount(amountInCents)

  return (
    <>
      <Box
        component={Link}
        to={`/account/payments/transactions/${id}`}
        className={`${classes.transactionRow} ${
          isLast ? classes.transactionRowLast : ''
        }`}
      >
        <Box className={classes.rowMain}>
          <Box
            className={`${classes.iconWrap} ${
              isRefund ? classes.refundIconWrap : classes.incomeIconWrap
            }`}
          >
            {isRefund ? (
              <MoneyOffIcon className={classes.rowIcon} fontSize="small" />
            ) : (
              <AttachMoneyIcon className={classes.rowIcon} fontSize="small" />
            )}
          </Box>
          <Box className={classes.rowCopy}>
            <Box className={classes.rowTitleLine}>
              <Typography className={classes.payorName}>{payorName}</Typography>
              {isRefund ? (
                <Chip
                  label={PAYMENT_STATUS_LABEL[status]}
                  size="small"
                  className={`${classes.statusChip} ${
                    classes[STATUS_CHIP_CLASS[status]]
                  }`}
                />
              ) : null}
            </Box>
            <Typography className={classes.rowMeta}>
              {[description, formattedDate].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
        </Box>
        <Typography
          className={`${classes.rowAmount} ${
            isRefund ? classes.rowAmountRefund : ''
          }`}
        >
          {formattedAmount}
        </Typography>
      </Box>
      {!isLast ? <Divider className={classes.rowDivider} /> : null}
    </>
  )
}

const TransactionHistory = () => {
  const classes = useStyles()
  const { isCoach } = useAuth()
  const [selectedMonth, setSelectedMonth] = useState(DateTime.now())
  const [searchQuery, setSearchQuery] = useState('')
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false)

  const { data, previousData, loading, error } = useTransactionHistoryQuery(
    selectedMonth.startOf('month'),
    selectedMonth.endOf('month'),
  )

  const registrationPayments = useMemo(() => {
    const payments =
      data?.registrationPayments || previousData?.registrationPayments || []

    return [...payments].sort(
      (left, right) =>
        DateTime.fromISO(right.createdAt).toMillis() -
        DateTime.fromISO(left.createdAt).toMillis(),
    )
  }, [data, previousData])

  const filteredPayments = useMemo(
    () => filterPaymentsBySearch(registrationPayments, searchQuery),
    [registrationPayments, searchQuery],
  )

  const hasActiveFilters =
    searchQuery.trim().length > 0 ||
    !selectedMonth.hasSame(DateTime.now(), 'month')

  const handleClearFilters = () => {
    setSearchQuery('')
    setSelectedMonth(DateTime.now())
  }

  const completedIncomeInCents = useMemo(
    () => (isCoach ? getCompletedIncomeInCents(registrationPayments) : 0),
    [isCoach, registrationPayments],
  )

  const csvData = useMemo(
    () => (isCoach ? generateTransactionsCsvData(registrationPayments) : []),
    [isCoach, registrationPayments],
  )

  const exportFilename = getTransactionsExportFilename(selectedMonth)
  const monthLabel = formatMonthRange(selectedMonth)
  const canExport = isCoach && registrationPayments.length > 0 && !loading
  const isSearchActive = searchQuery.trim().length > 0

  const tableContent = (() => {
    if (loading && registrationPayments.length === 0) {
      return <LinearProgress color="primary" />
    }

    if (error) return <Error error={error} />

    if (filteredPayments.length === 0) {
      return (
        <Box className={classes.emptyStateContainer}>
          <SectionEmptyState
            Icon={ReceiptIcon}
            title="No transactions"
            caption={
              isSearchActive
                ? 'No transactions match your search. Try a different name.'
                : 'No results for the selected date range. Transactions that occur within the selected dates will appear here.'
            }
            bordered={false}
          />
        </Box>
      )
    }

    return filteredPayments.map((registrationPayment, index) => (
      <TransactionHistoryRow
        key={registrationPayment.id}
        registrationPayment={registrationPayment}
        isLast={index === filteredPayments.length - 1}
      />
    ))
  })()

  return (
    <>
      <PageHeader
        title="Transactions"
        crumbs={paymentsCrumb}
        actions={
          isCoach ? (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<FileDownloadOutlinedIcon />}
              disabled={!canExport}
              onClick={() => setIsExportDialogOpen(true)}
            >
              Export CSV
            </Button>
          ) : null
        }
      />
      {isCoach ? (
        <ExportTransactionsDialog
          open={isExportDialogOpen}
          onClose={() => setIsExportDialogOpen(false)}
          filename={exportFilename}
          csvData={csvData}
          monthLabel={monthLabel}
        />
      ) : null}
      <Box className={classes.contentContainer}>
        <Box className={classes.stack}>
          {isCoach ? (
            <IncomeSummaryCard
              selectedMonth={selectedMonth}
              incomeInCents={completedIncomeInCents}
            />
          ) : null}
          <AccountCard
            title="Recent transactions"
            hideDivider
            headerContent={
              <TransactionFilterRow
                searchQuery={searchQuery}
                onSearchQueryChange={setSearchQuery}
                selectedMonth={selectedMonth}
                onMonthChange={setSelectedMonth}
              />
            }
            action={
              hasActiveFilters ? (
                <Button
                  className={classes.clearFiltersButton}
                  onClick={handleClearFilters}
                  startIcon={<CloseIcon fontSize="small" />}
                >
                  Clear filters
                </Button>
              ) : null
            }
            classes={classes}
          >
            <Box className={classes.filterDivider} />
            {tableContent}
          </AccountCard>
        </Box>
      </Box>
    </>
  )
}

export default TransactionHistory
